import type { LucideIcon } from 'lucide-react';
import { CheckCircle2, Circle, Loader2, Workflow, X } from 'lucide-react';
import React from 'react';
import { Timer } from './Timer';
import { cn } from '@/lib/utils';

interface SidebarItemProps {
  id: string;
  title: string;
  description: string;
  status: 'not_started' | 'scheduled' | 'in_progress' | 'completed' | 'failed';
  isActive: boolean;
  onClick: () => void;
  startTime?: string;
  endTime?: string;
  type: 'workflow' | 'task';
  customIcon?: {
    icon: LucideIcon;
    bgColor: string;
    textColor: string;
  };
}

export const SidebarItem: React.FC<SidebarItemProps> = ({
  title,
  status,
  isActive,
  onClick,
  startTime,
  endTime,
  type,
  customIcon,
}) => {
  const isDisabled = status === 'not_started';

  const StatusIcon = () => {
    if (customIcon) {
      const Icon = customIcon.icon;
      return (
        <div
          className={cn(
            'p-1.5 rounded-lg transition-colors',
            customIcon.bgColor,
            isDisabled && 'opacity-40'
          )}
        >
          <Icon size={18} className={customIcon.textColor} />
        </div>
      );
    }

    if (type === 'workflow') {
      return (
        <div
          className={cn(
            'p-2 rounded-lg bg-violet-100 dark:bg-violet-500/20',
            isDisabled && 'opacity-40'
          )}
        >
          <Workflow size={18} className="text-violet-600 dark:text-violet-400" />
        </div>
      );
    }

    const iconConfig = {
      completed: {
        bg: 'bg-emerald-100 dark:bg-emerald-500/20',
        icon: <CheckCircle2 size={16} className="text-emerald-600 dark:text-emerald-400" />,
      },
      in_progress: {
        bg: 'bg-blue-100 dark:bg-blue-500/20',
        icon: <Loader2 size={16} className="text-blue-600 dark:text-blue-400 animate-spin" />,
      },
      scheduled: {
        bg: 'bg-amber-100 dark:bg-amber-500/20',
        icon: <Loader2 size={16} className="text-amber-600 dark:text-amber-400 animate-spin" />,
      },
      failed: {
        bg: 'bg-red-100 dark:bg-red-500/20',
        icon: <X size={16} className="text-red-600 dark:text-red-400" />,
      },
      not_started: {
        bg: 'bg-zinc-100 dark:bg-zinc-800',
        icon: <Circle size={16} className="text-zinc-400 dark:text-zinc-500" />,
      },
    };

    const config = iconConfig[status] || iconConfig.not_started;

    return (
      <div
        className={cn('p-1.5 rounded-lg transition-colors', config.bg, isDisabled && 'opacity-40')}
      >
        {config.icon}
      </div>
    );
  };

  return (
    <button
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      className={cn(
        'w-full text-left px-4 py-3 transition-all duration-150',
        'border-l-2 border-transparent',
        isDisabled ? 'cursor-default opacity-60' : 'cursor-pointer',
        isActive
          ? 'bg-zinc-100 dark:bg-zinc-800 border-l-zinc-900 dark:border-l-zinc-100'
          : 'hover:bg-zinc-50 dark:hover:bg-zinc-800/50',
        type === 'workflow' && 'border-b border-zinc-100 dark:border-zinc-800'
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <StatusIcon />
          <span
            className={cn(
              'truncate',
              type === 'workflow' ? 'text-sm font-semibold' : 'text-sm',
              isActive ? 'text-zinc-900 dark:text-zinc-100' : 'text-zinc-700 dark:text-zinc-300',
              isDisabled && 'text-zinc-400 dark:text-zinc-500'
            )}
          >
            {title}
          </span>
        </div>
        {startTime && !isDisabled && (
          <Timer startTime={startTime} endTime={endTime} status={status} />
        )}
      </div>
    </button>
  );
};
